/**
 * FABTEXS AI — shared Web Push registration (buyer + seller).
 *
 * WHY THIS MODULE EXISTS
 * ----------------------
 * Before this, the ONLY way a buyer could ever obtain a push subscription was
 * the catalogue onboarding sheet, and that sheet is deliberately suppressed
 * when the app runs as an installed PWA (`display-mode: standalone`). So the
 * one configuration where closed-app push actually matters — the installed
 * PWA — could never subscribe, and nothing ever re-subscribed after a PWA
 * reinstall or a browser-side subscription rotation. Result: zero rows in
 * `push_subscriptions`, hence no buyer notification when the app was closed.
 *
 * The fix is a single idempotent, self-healing sync that runs on EVERY buyer /
 * seller app load:
 *   • if notification permission is already granted, silently (re)create the
 *     subscription and re-POST it to the backend (server upserts on endpoint,
 *     so repeats are free);
 *   • it never prompts on load — a prompt is only raised from a real user
 *     gesture via `requestPermission: true`.
 *
 * An in-page poller (the seller ka-ching layer / the buyer bell) can only fire
 * while the page is alive, so it dies when Android freezes or evicts the app.
 * A Service Worker push is the only mechanism the OS wakes regardless of screen
 * state — which is why this registration is what makes closed/locked-phone
 * notifications possible at all.
 */
import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL;
const BUYER_KEY = 'fabtexs.buyer.id';
const BUYER_COOKIE = 'fabtexs_buyer_id';
// Stable per-BROWSER id (distinct from the buyer id, which is per person and
// shared across their devices). Lets the server retire a superseded endpoint
// for THIS browser without touching the same owner's other devices.
const DEVICE_KEY = 'fabtexs.device.id';
const DEVICE_COOKIE = 'fabtexs_device_id';
// Cache entry the Service Worker reads so it can re-register a rotated
// subscription on its own (see `pushsubscriptionchange` in /sw.js).
const CFG_CACHE = 'fabtexs-push-cfg';
const CFG_URL = '/__fabtexs_push_cfg';

/* ── buyer identity ──────────────────────────────────────────────────────── */

const readCookie = (name) => {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  } catch (_e) { return null; }
};

const writeCookie = (name, value) => {
  try {
    // 2 years, same-site — a durable mirror of the localStorage identity.
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=63072000; SameSite=Lax`;
  } catch (_e) { /* noop */ }
};

/**
 * The STABLE per-browser buyer id (`fabtexs.buyer.id`) — the same identity
 * already used by buyer_chats / orders / buyer_connections / notifications.
 * Resolution order: localStorage → cookie mirror → freshly minted.
 *
 * The cookie mirror matters for notifications specifically: losing this id
 * orphans the buyer from every `buyer_connections` row and every existing
 * `buyer_notifications` record, so the bell goes empty and the server-side
 * fanout stops addressing them. Restoring from the mirror keeps the SAME id,
 * so it can only ever preserve continuity, never change it.
 */
export function getBuyerId() {
  let sid = null;
  try { sid = localStorage.getItem(BUYER_KEY); } catch (_e) { /* disabled */ }
  if (!sid) {
    const fromCookie = readCookie(BUYER_COOKIE);
    if (fromCookie) {
      sid = fromCookie;
      try { localStorage.setItem(BUYER_KEY, sid); } catch (_e) { /* noop */ }
    }
  }
  if (!sid) {
    sid = `b-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    try { localStorage.setItem(BUYER_KEY, sid); } catch (_e) { /* noop */ }
  }
  writeCookie(BUYER_COOKIE, sid);
  return sid;
}

/**
 * Stable id for this browser install. Used only to retire a stale push endpoint
 * belonging to this same browser (see `save_subscription`), never for identity.
 */
export function getDeviceId() {
  let did = null;
  try { did = localStorage.getItem(DEVICE_KEY); } catch (_e) { /* disabled */ }
  if (!did) did = readCookie(DEVICE_COOKIE);
  if (!did) {
    did = `d-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }
  try { localStorage.setItem(DEVICE_KEY, did); } catch (_e) { /* noop */ }
  writeCookie(DEVICE_COOKIE, did);
  return did;
}

/* ── low-level helpers ───────────────────────────────────────────────────── */

/** base64url VAPID public key → Uint8Array for pushManager.subscribe(). */
export function urlBase64ToUint8Array(b64) {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const s = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(s);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

const bufToB64Url = (buf) => {
  try {
    const bytes = new Uint8Array(buf);
    let s = '';
    bytes.forEach((b) => { s += String.fromCharCode(b); });
    return window.btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  } catch (_e) { return null; }
};

/** Register the shared service worker (idempotent) and wait until it's active. */
export async function ensureServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    await navigator.serviceWorker.register('/sw.js');
    return await navigator.serviceWorker.ready;
  } catch (_e) { return null; }
}

/**
 * Hand the Service Worker the API base URL so it can re-register a rotated
 * subscription without a page open. Best-effort; the SW falls back to its own
 * origin if this is unavailable.
 */
async function publishSwConfig() {
  try {
    if (!('caches' in window) || !BASE) return;
    const cache = await caches.open(CFG_CACHE);
    await cache.put(CFG_URL, new Response(JSON.stringify({ base: BASE }),
      { headers: { 'Content-Type': 'application/json' } }));
  } catch (_e) { /* noop */ }
}

/**
 * Core sync. Returns one of:
 *   'unsupported' | 'denied' | 'default' (no prompt raised) | 'synced' | 'error'
 *
 * @param {'buyer'|'seller'} kind
 * @param {object}  opts
 * @param {boolean} opts.requestPermission  Prompt if undecided. MUST be called
 *                                          straight from a user gesture.
 * @param {string}  opts.buyerId            Required for kind === 'buyer'.
 * @param {Function} opts.post              (subscriptionJson) => Promise
 */
// One in-flight sync per role. React StrictMode double-invokes effects in dev,
// and two surfaces can mount at once, so concurrent callers used to each run
// `pushManager.subscribe()` and Chrome handed back TWO different endpoints for
// the same browser — which the device then renders as two identical banners.
// Concurrent callers now share a single result.
const inFlight = {};

async function syncPush(kind, { requestPermission = false, buyerId = null, post }) {
  if (inFlight[kind]) return inFlight[kind];
  const run = _syncPush(kind, { requestPermission, buyerId, post })
    .finally(() => { delete inFlight[kind]; });
  inFlight[kind] = run;
  return run;
}

async function _syncPush(kind, { requestPermission = false, buyerId = null, post }) {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)
        || !('Notification' in window)) return 'unsupported';

    let perm = Notification.permission;
    if (perm === 'default') {
      // Never prompt on a plain page load — only from an explicit gesture.
      if (!requestPermission) return 'default';
      perm = await Notification.requestPermission();
    }
    if (perm !== 'granted') return 'denied';

    const reg = await ensureServiceWorker();
    if (!reg || !reg.pushManager) return 'unsupported';

    const { data: k } = await axios.get(`${BASE}/api/push/vapid-key`);
    if (!k || !k.key) return 'error';
    const appKey = k.key.replace(/=+$/, '');

    let sub = await reg.pushManager.getSubscription();
    // If the server's VAPID key was rotated, an existing subscription is bound
    // to the OLD key and every send to it fails — drop it and re-subscribe.
    if (sub) {
      const bound = bufToB64Url(sub.options && sub.options.applicationServerKey);
      if (bound && bound !== appKey) {
        try { await sub.unsubscribe(); } catch (_e) { /* noop */ }
        sub = null;
      }
    }
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(appKey),
      });
    }
    if (!sub) return 'error';

    // Idempotent: the backend upserts on subscription.endpoint.
    await post(sub.toJSON());
    await publishSwConfig();
    return 'synced';
  } catch (_e) {
    return 'error';
  }
}

/**
 * Is web push PROVEN to be delivering for this owner?
 *
 * This decides whether the in-app notification fallback may stand down, so the
 * bar is deliberately high: the server must report a real subscription AND a
 * recent send that the push service accepted AND that the device confirmed it
 * actually rendered (`/api/push/receipt`).
 *
 * The previous version of this check asked only "does a PushSubscription
 * object exist in this browser?" — which proves nothing about delivery. Seller
 * push had never once delivered, yet that check returned true the moment a
 * subscription was created, so the in-app checker stood down and sellers lost
 * their working closed-app notification with nothing replacing it. Never
 * weaken this back to a local-only check.
 *
 * Fails CLOSED: any error, any uncertainty → `false` → the fallback keeps
 * showing notifications. A duplicate banner is a far smaller failure than a
 * missing one.
 *
 * Result is cached briefly so a 12s poll doesn't hammer the endpoint.
 */
const HEALTH_TTL_MS = 60000;
const healthCache = {};   // key -> { at, confirmed }

export async function isPushDeliveryConfirmed({ ownerKind, ownerId } = {}) {
  try {
    if (!ownerKind) return false;
    // No local subscription → push certainly isn't delivering here.
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg || !reg.pushManager) return false;
    if (!(await reg.pushManager.getSubscription())) return false;

    const key = `${ownerKind}:${ownerId || 'self'}`;
    const hit = healthCache[key];
    if (hit && Date.now() - hit.at < HEALTH_TTL_MS) return hit.confirmed;

    let data;
    if (ownerKind === 'seller') {
      const token = sessionStorage.getItem('fab_imp_token') || localStorage.getItem('fab_token');
      if (!token) return false;
      ({ data } = await axios.get(`${BASE}/api/push/health/seller`,
        { headers: { Authorization: `Bearer ${token}` } }));
    } else {
      if (!ownerId) return false;
      ({ data } = await axios.get(`${BASE}/api/push/health`,
        { params: { owner_kind: 'buyer', owner_id: ownerId } }));
    }
    const confirmed = !!(data && data.confirmed);
    healthCache[key] = { at: Date.now(), confirmed };
    return confirmed;
  } catch (_e) {
    return false;                                  // fail closed
  }
}

/* ── public API ──────────────────────────────────────────────────────────── */

/**
 * Buyer push sync. Safe (and cheap) to call on every buyer app load — that
 * repetition is exactly what heals a lost subscription after a PWA reinstall,
 * a cleared site-data, or a browser-side endpoint rotation.
 */
export function syncBuyerPush({ requestPermission = false, buyerId = null } = {}) {
  const bid = buyerId || getBuyerId();
  if (!bid) return Promise.resolve('error');
  return syncPush('buyer', {
    requestPermission,
    buyerId: bid,
    post: (subscription) => axios.post(`${BASE}/api/push/subscribe-buyer`, {
      session_id: bid, subscription, device_id: getDeviceId(),
    }),
  });
}

/**
 * Seller push sync — additive. The existing dashboard poller + ka-ching are
 * untouched; this only adds the OS-level delivery path that survives the app
 * being closed or the phone being locked (a poller cannot).
 */
export function syncSellerPush({ requestPermission = false } = {}) {
  return syncPush('seller', {
    requestPermission,
    post: (subscription) => {
      const token = sessionStorage.getItem('fab_imp_token') || localStorage.getItem('fab_token');
      if (!token) return Promise.resolve();      // not logged in — nothing to bind
      return axios.post(`${BASE}/api/push/subscribe-seller`,
        { subscription, device_id: getDeviceId() },
        { headers: { Authorization: `Bearer ${token}` } });
    },
  });
}
